import styled from "styled-components";
import {
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  TooltipHost,
} from "@fluentui/react";
import React, { Fragment, useContext, useState } from "react";
import { ApplyButton } from "./ApplyButton";
import { ColumnList } from "./ColumnList";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectAllCheckbox } from "./SelectAllCheckbox";
import { ViewContextButton } from "./ViewContextButton";
import { ViewDescription } from "./ViewDescription";
import { ViewDialog } from "./ViewDialog";
import { ViewSelect } from "./ViewSelect";

export type ViewDialogState = {
  isOpen: boolean;
  viewId: string | null;
};

const initStateViewDialog: ViewDialogState = {
  isOpen: false,
  viewId: null,
};

export const ColumnOptionsPanel = () => {
  const [viewDialog, setViewDialog] =
    useState<ViewDialogState>(initStateViewDialog);
  const {
    isReady,
    selectedView,
    closePanel,
    renderAdditionalOptions,
    strings,
    additionalOptionsHeight,
    additionalOptionsStyle,
    disableButtons,
    onResetAllStorage,
  } = useContext(ColumnOptionsContext);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const shouldShowDescription =
    selectedView &&
    typeof selectedView.description === "string" &&
    selectedView.description.length > 0;

  return (
    <Panel
      isOpen
      onDismiss={closePanel}
      type={PanelType.custom}
      customWidth="340px"
      isLightDismiss
      hasCloseButton={false}
      onRenderHeader={() => (
        <PanelHeader>
          <HeaderTitle>{strings.headerLabel}</HeaderTitle>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close"
            onClick={closePanel}
          />
        </PanelHeader>
      )}
      onRenderFooterContent={() => (
        <Footer>
          <ApplyButton />
          {onResetAllStorage && (
            <TooltipHost content={strings.resetAllStorageTooltip}>
              <IconButton
                iconProps={{ iconName: "RevToggleKey" }}
                onClick={() => setIsResetDialogOpen(true)}
                styles={{
                  root: { marginLeft: "auto" },
                  icon: { fontSize: 16 },
                }}
              />
            </TooltipHost>
          )}
        </Footer>
      )}
      isFooterAtBottom
      styles={{
        scrollableContent: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        content: {
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: 0,
          overflow: "hidden",
        },
        footer: {
          borderTop: "none",
          padding: 0,
        },
      }}
    >
      {!isReady ? (
        <SpinnerContainer>
          <Spinner size={SpinnerSize.large} />
        </SpinnerContainer>
      ) : (
        <Fragment>
          <ViewContainer>
            <ViewSelectWrapper>
              <ViewSelect />
            </ViewSelectWrapper>
            <ViewContextButton
              setViewDialog={setViewDialog}
              disableButtons={disableButtons}
            />
          </ViewContainer>
          <DescriptionContainer>
            {shouldShowDescription && (
              <ViewDescriptionWrapper>
                <ViewDescription />
              </ViewDescriptionWrapper>
            )}
            <SelectAllCheckbox />
          </DescriptionContainer>
          <ColumnList />
          {renderAdditionalOptions && (
            <AdditionalOptionsContainer
              height={additionalOptionsHeight}
              style={additionalOptionsStyle?.container}
            >
              {renderAdditionalOptions()}
            </AdditionalOptionsContainer>
          )}
          {viewDialog.isOpen && (
            <ViewDialog
              viewId={viewDialog.viewId}
              close={() => setViewDialog(initStateViewDialog)}
            />
          )}
        </Fragment>
      )}
      {isResetDialogOpen && (
        <Dialog
          hidden={false}
          onDismiss={() => setIsResetDialogOpen(false)}
          dialogContentProps={{
            type: DialogType.normal,
            title: strings.resetAllStorageTitle,
            subText: strings.resetAllStorageMessage,
          }}
          modalProps={{ isBlocking: true }}
        >
          <DialogFooter>
            <PrimaryButton
              onClick={() => {
                setIsResetDialogOpen(false);
                onResetAllStorage?.();
              }}
              text={strings.resetAllStorageConfirm}
            />
            <DefaultButton
              onClick={() => setIsResetDialogOpen(false)}
              text={strings.resetAllStorageCancel}
            />
          </DialogFooter>
        </Dialog>
      )}
    </Panel>
  );
};

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 8px 24px;
  width: 100%;
`;

const HeaderTitle = styled.span`
  font-size: 20px;
  font-weight: 600;
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const ViewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px 4px 16px;
`;

const ViewSelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 26px 4px 16px;
  gap: 8px;
  min-height: 24px;
`;

const ViewDescriptionWrapper = styled.div`
  flex: 1;
`;

const AdditionalOptionsContainer = styled.div<{ height?: number }>`
  height: ${(props) => props.height || 80}px;
  padding: 0 16px;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
